// Auth context: subscribes to Supabase session changes and fetches our
// platform-side state (approved, roles, god mode) into one place. Routes
// read this via useAuth() and gate themselves accordingly.
import { createContext, useContext, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import { fetchMe, type MeResponse, ApiError } from "./api";

type Status = "loading" | "signed-out" | "pending" | "approved";

type AuthContextValue = {
  status: Status;
  session: Session | null;
  me: MeResponse | null;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<Status>("loading");

  async function refresh() {
    const {
      data: { session: current },
    } = await supabase.auth.getSession();
    setSession(current);
    if (!current) {
      setMe(null);
      setStatus("signed-out");
      return;
    }
    try {
      const result = await fetchMe();
      setMe(result);
      setStatus(result.approved ? "approved" : "pending");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setStatus("signed-out");
      } else {
        console.error("[auth] /me failed", err);
        // On unexpected errors, fall back to pending so user lands somewhere
        // sensible; they can re-trigger refresh from the pending page.
        setStatus("pending");
      }
    }
  }

  useEffect(() => {
    void refresh();
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      void refresh();
    });
    return () => sub.subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
    setSession(null);
    setMe(null);
    setStatus("signed-out");
  }

  return (
    <AuthContext.Provider value={{ status, session, me, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
